import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslate } from '@growtherapy/i18n';
import { Alert } from '@growtherapy/sprout-ui';

import { RequiredAgreementsSimple, SimplifiedRequiredAgreementsProps } from '@grow-therapy-team/seedling-components';

import { LoginButton, LoginButtonProps } from '../auth/LoginButton';
import { LoginSectionProps } from '../auth/LoginSection';
import { PIIVerification as BasePIIVerification, PIIVerificationProps } from '../required-agreements/PIIVerification';
import { AVPermissionCallout } from './AVPermissionCallout';
import { ContactSupportButton } from './ContactSupportButton';
import { InputControls, InputControlsProps } from './InputControls';
import { SeparateAccountCallout } from './SeparateAccountCallout';

function Container({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={twMerge('flex flex-col gap-8 sm:gap-500 max-sm:px-350 max-sm:pb-350', className)}>{children}</div>
  );
}

export type RightContentDefaultProps = InputControlsProps & {
  inputControlsComponent?: FC<InputControlsProps>;
  loginSectionComponent: FC<LoginSectionProps>;
};

function Default({
  inputControlsComponent: InputControlsComponent = InputControls,
  loginSectionComponent: LoginSectionComponent,
  ...inputControlsProps
}: RightContentDefaultProps) {
  return (
    <Container>
      <LoginSectionComponent
        className='items-start'
        // To match the label styling in the new intro room content
        instructionClassName='max-sm:rebrand:typography-subhead-mobile-small max-sm:text-left sm:rebrand:typography-label-small sm:rebrand:text-text-default sm:rebrand:!font-semibold'
      />
      <InputControlsComponent className='gap-8 sm:gap-500' {...inputControlsProps}>
        <AVPermissionCallout />
      </InputControlsComponent>
    </Container>
  );
}

export type RightContentPIIVerificationProps = PIIVerificationProps &
  LoginButtonProps & {
    loginButtonComponent?: FC<LoginButtonProps>;
    piiVerificationComponent?: FC<PIIVerificationProps>;
  };

function PIIVerification({
  loginButtonComponent: LoginButtonComponent = LoginButton,
  useClientLogin,
  ...piiVerificationProps
}: RightContentPIIVerificationProps) {
  return (
    <Container className='gap-500 items-center'>
      <BasePIIVerification {...piiVerificationProps} />
      <LoginButtonComponent useClientLogin={useClientLogin} />
    </Container>
  );
}

type RequiredAgreementsProps = InputControlsProps & {
  contactUsUrl: string;
  contactSupportUrl: string;
  hasSubmissionError?: boolean;
  inputControlsComponent?: FC<InputControlsProps>;
  requiredAgreementsProps: SimplifiedRequiredAgreementsProps;
  shouldSeeSeparateAccountCallout: boolean;
};

function RequiredAgreements({
  contactUsUrl,
  contactSupportUrl,
  hasSubmissionError,
  inputControlsComponent: InputControlsComponent = InputControls,
  requiredAgreementsProps,
  shouldSeeSeparateAccountCallout,
  ...inputControlsProps
}: RequiredAgreementsProps) {
  const t = useTranslate();
  // If we're showing the full parent/guardian input form, we'll render the
  // visitor name input field inline with that grid. Otherwise, we'll render it
  // as a separate field above the required agreements form.
  const showInlineVisitorName = requiredAgreementsProps.isMinor && !requiredAgreementsProps.hasParentGuardian;

  return (
    <Container className='sm:gap-450'>
      {hasSubmissionError && (
        <Alert
          className='mb-250'
          use='error'
          heading={t('telehealth-core:errorsCore.somethingWentWrong')}
          actions={<ContactSupportButton contactSupportUrl={contactSupportUrl} />}
        >
          {t('telehealth-core:errorsCore.pleaseRefreshThePageToTry')}
        </Alert>
      )}
      {!showInlineVisitorName && (
        <InputControlsComponent
          {...inputControlsProps}
          // If present, this field is the first focusable input field on the
          // page and is part of a form that is the primary purpose of that
          // page. Autofocusing it will not skip over any other important
          // meaning or context on the page, so this is a situation where it's
          // appropriate and good UX to autofocus it.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          inputLabel={requiredAgreementsProps.isMinor ? t('telehealth-core:clientInfo.clientsChosenName') : undefined}
        />
      )}
      <RequiredAgreementsSimple
        {...requiredAgreementsProps}
        autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        formTitleOrInstructions={t('telehealth-core:introRoom.forParentsAndGuardiansEnterYour')}
        guardianFormAdditionalField={
          showInlineVisitorName ? (
            <InputControlsComponent
              {...inputControlsProps}
              inputLabel={t('telehealth-core:clientInfo.clientsChosenName')}
              disabled={requiredAgreementsProps.isSubmitting}
            />
          ) : undefined
        }
      />
      <AVPermissionCallout />
      {shouldSeeSeparateAccountCallout && <SeparateAccountCallout contactUsUrl={contactUsUrl} />}
    </Container>
  );
}

export const RightContent = {
  Default,
  PIIVerification,
  RequiredAgreements,
};
